import { ILegalClient } from '../Interfaces/ILegalClient';
import { ILegalTransactionRelationship } from '../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';

// --- Constants ---
export const TESTATOR_MEMBER_ROLE_ID = 5;
export const CHILD_MEMBER_ROLE_ID = 8;
export const PARENT_MEMBER_ROLE_ID = 10;
export const SPOUSE_MEMBER_ROLE_ID = 6; // Represents Ehepartner
export const MARRIAGE_RELATIONSHIP_TYPE_ID = 5; // Represents Ehe
export const FAMILY_RELATIONSHIP_TYPE_ID = 6;
export const MARRIED_RELATIONSHIP_TYPES = [
  TESTATOR_MEMBER_ROLE_ID,
  SPOUSE_MEMBER_ROLE_ID,
];
export const INHERITANCE_RELATIONSHIP_TYPE_ID = 10;

export enum TestatorRelationshipType {
  Testator = 'Testator',
  Parent = 'Parent',
  Child = 'Child',
  Grandchild = 'Grandchild',
  None = 'None', // Represents no direct hierarchical relationship to a testator
}

// --- Helper Functions (Optimized to return Sets of IDs) ---

export const getTestatorIds = (
  relationships: ILegalTransactionRelationship[]
): Set<number> => {
  const ids = new Set<number>();
  relationships.forEach(rel => {
    rel.Members.forEach(member => {
      if (member.idRelationshipRole === TESTATOR_MEMBER_ROLE_ID) {
        ids.add(member.idLegalClient);
      }
    });
  });
  return ids;
};

const getParentIds = (
  testatorIds: Set<number>,
  relationships: ILegalTransactionRelationship[]
): Set<number> => {
  const ids = new Set<number>();
  relationships.forEach(rel => {
    const containsTestatorAsChild = rel.Members.some(
      member =>
        member.idRelationshipRole === CHILD_MEMBER_ROLE_ID &&
        testatorIds.has(member.idLegalClient)
    );
    if (containsTestatorAsChild) {
      rel.Members.forEach(member => {
        if (member.idRelationshipRole === PARENT_MEMBER_ROLE_ID) {
          ids.add(member.idLegalClient);
        }
      });
    }
  });
  return ids;
};

const getChildrenIds = (
  testatorIds: Set<number>,
  relationships: ILegalTransactionRelationship[]
): Set<number> => {
  const ids = new Set<number>();
  relationships.forEach(rel => {
    const containsTestatorAsParent = rel.Members.some(
      member =>
        member.idRelationshipRole === PARENT_MEMBER_ROLE_ID &&
        testatorIds.has(member.idLegalClient)
    );
    if (containsTestatorAsParent) {
      rel.Members.forEach(member => {
        if (member.idRelationshipRole === CHILD_MEMBER_ROLE_ID) {
          ids.add(member.idLegalClient);
        }
      });
    }
  });
  return ids;
};

const getGrandchildrenIds = (
  childrenIds: Set<number>,
  relationships: ILegalTransactionRelationship[]
): Set<number> => {
  const ids = new Set<number>();
  relationships.forEach(rel => {
    const containsChildAsParent = rel.Members.some(
      member =>
        member.idRelationshipRole === PARENT_MEMBER_ROLE_ID &&
        childrenIds.has(member.idLegalClient)
    );
    if (containsChildAsParent) {
      rel.Members.forEach(member => {
        if (member.idRelationshipRole === CHILD_MEMBER_ROLE_ID) {
          ids.add(member.idLegalClient);
        }
      });
    }
  });
  return ids;
};

// --- Primary Function ---

/**
 * Determines the hierarchical relationship of a specific legal client to the testators.
 * @param client The ILegalClient to check.
 * @param relationships The array of all legal transaction relationships.
 * @returns A TestatorRelationshipType enum value.
 */
export const getRelationshipToTestator = (
  client: ILegalClient,
  relationships: ILegalTransactionRelationship[]
): TestatorRelationshipType => {
  // 1. Calculate all relationship groups once.
  const testatorIds = getTestatorIds(relationships);
  const childrenIds = getChildrenIds(testatorIds, relationships);
  const grandchildrenIds = getGrandchildrenIds(childrenIds, relationships);
  const parentIds = getParentIds(testatorIds, relationships);

  const clientId = client.idLegalClient;

  // 2. Check the client's ID against the sets in a logical order.
  if (testatorIds.has(clientId)) {
    return TestatorRelationshipType.Testator;
  }
  if (grandchildrenIds.has(clientId)) {
    return TestatorRelationshipType.Grandchild;
  }
  if (childrenIds.has(clientId)) {
    return TestatorRelationshipType.Child;
  }
  if (parentIds.has(clientId)) {
    return TestatorRelationshipType.Parent;
  }

  return TestatorRelationshipType.None;
};
